import React from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shared FilterPanel container — single visual pattern reused across
 * Admin Transactions, Orders, Customers, Tickets, and their Customer
 * counterparts.
 *
 * Props:
 *   open              — controlled expanded/collapsed state
 *   onToggle          — () => void; flips `open`
 *   activeChips       — array of { key, label, value, onRemove } objects
 *   onClearAll        — () => void; resets every filter
 *   onApply           — optional () => void; if omitted, the panel is
 *                       considered live-filtered (no Apply button)
 *   advancedOpen      — controlled "Advanced filters" expanded state
 *   onToggleAdvanced  — () => void
 *   advancedActiveCount — number for the "Advanced filters (N active)" label
 *   children          — the basic filter fields (always-rendered when open)
 *   advancedChildren  — the advanced filter fields (rendered only when
 *                       advancedOpen)
 *
 * Visual rules (per design_guidelines.json):
 *   - Header: <Filter> icon + "Filters" title + active-count badge + Hide/Show
 *   - Body: collapsible, default open on desktop, closed on mobile
 *   - Chips row: shown above body when `activeChips` is non-empty
 *   - Footer: "Apply" (primary, optional) + "Clear" (secondary)
 *   - Advanced toggle: between basic body and footer
 */
export default function FilterPanel({
  open,
  onToggle,
  activeChips = [],
  onClearAll,
  onApply,
  advancedOpen = false,
  onToggleAdvanced,
  advancedActiveCount = 0,
  children,
  advancedChildren,
  headerExtra,
  testid = 'filter-panel',
  labels = {},
}) {
  const L = {
    filters: 'Filters',
    hide: 'Hide',
    show: 'Show',
    clear_all: 'Clear all',
    apply: 'Apply',
    clear: 'Clear',
    advanced_filters: 'Advanced filters',
    active: 'active',
    hide_filters: 'Hide filters',
    show_filters: 'Show filters',
    ...labels,
  };
  const activeCount = activeChips.length;
  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04)] mb-3" data-testid={testid}>
      {/* Header — `button` is the toggle, but a sibling slot (`headerExtra`)
          lets pages render things like a Refresh button without
          re-creating their own header bar. */}
      <div className="w-full flex items-center justify-between px-3 py-1.5 gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
          data-testid={`${testid}-header`}
        >
          <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          <span className="text-xs font-semibold text-slate-700">{L.filters}</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          {headerExtra}
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 text-[11px] text-slate-500 font-medium hover:text-slate-700"
            data-testid={`${testid}-toggle`}
            aria-label={open ? L.hide_filters : L.show_filters}
          >
            {open ? L.hide : L.show}
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Active chips */}
      {activeCount > 0 && (
        <div className="px-3 pb-1.5 flex flex-wrap items-center gap-1" data-testid={`${testid}-chips`}>
          {activeChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 pl-1.5 pr-0.5 py-px bg-cyan-50 border border-cyan-200 text-cyan-800 rounded-full text-[10px] font-medium"
              data-testid={`${testid}-chip-${chip.key}`}
            >
              <span className="text-cyan-600/80 font-semibold">{chip.label}:</span>
              <span className="font-mono text-cyan-900 max-w-[140px] truncate">{String(chip.value)}</span>
              <button
                type="button"
                onClick={chip.onRemove}
                className="w-3.5 h-3.5 inline-flex items-center justify-center rounded-full hover:bg-cyan-200 text-cyan-700"
                aria-label={`Clear ${chip.label}`}
                data-testid={`${testid}-chip-${chip.key}-remove`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold text-rose-600 hover:bg-rose-50 rounded"
            data-testid={`${testid}-clear-all`}
          >
            {L.clear_all}
          </button>
        </div>
      )}

      {/* Body — Phase-2 (08/Jun/2026) made significantly denser at the
          user's request: switched from `grid grid-cols-N` to a
          `flex flex-wrap items-end` layout so each filter takes its
          natural width (set by the inner input/select) instead of
          stretching to fill a grid cell. `items-end` aligns the
          BOTTOM edges of every cell so taller cells (Date with its
          quick-pill row below) no longer push their row neighbours
          off-axis. Each filter child opts into a sensible default
          via the `.ar-filter-cell` class hooked in `index.css`. */}
      {open && (
        <div className="border-t border-slate-100 px-3 py-2" data-testid={`${testid}-body`}>
          <div className="ar-filter-grid flex flex-wrap items-end gap-x-2 gap-y-2">
            {children}
            <div className="flex gap-1 items-end ml-auto">
              {onApply && (
                <button
                  type="button"
                  onClick={onApply}
                  className="h-8 px-2.5 rounded text-[11px] font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow transition-shadow whitespace-nowrap"
                  data-testid={`${testid}-apply`}
                >
                  {L.apply}
                </button>
              )}
              <button
                type="button"
                onClick={onClearAll}
                className="h-8 px-2.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors whitespace-nowrap"
                data-testid={`${testid}-clear`}
              >
                {L.clear}
              </button>
            </div>
          </div>

          {/* Advanced toggle + body */}
          {advancedChildren && (
            <>
              <button
                type="button"
                onClick={onToggleAdvanced}
                className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-cyan-700 hover:text-cyan-900"
                data-testid={`${testid}-advanced-toggle`}
              >
                {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {L.advanced_filters} {advancedActiveCount > 0 && <span className="text-slate-500 font-normal">({advancedActiveCount} {L.active})</span>}
              </button>
              {advancedOpen && (
                <div className="ar-filter-grid mt-1.5 pt-1.5 border-t border-dashed border-slate-200 flex flex-wrap items-end gap-x-2 gap-y-2" data-testid={`${testid}-advanced-body`}>
                  {advancedChildren}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
